import React from 'react';
import Input from '../Input/Input';
import RadioComponent from '../Radio/Radio';
import Select from '../Select/Select';
import Transfer from '../Transfer/Transfer';
import Subtitle from 'components/Subtitle/Subtitle';
import type { TransferDirection } from 'antd/es/transfer';
import CheckboxGroupComponent from '../GroupCheckbox/GroupCheckbox';
import { Control, FieldValues, useWatch } from 'react-hook-form';
import { useExtraQuestionsData } from 'context/ExtraQuestionsContext';

type ActivityCode = 'P' | 'A' | 'S';

interface DynamicQuestionProps {
  sectionList: {
    items: Array<any>;
  };
  questionList: {
    type: string;
    value: string;
    text: string;
    items?: any[];
    dependency: any;
    lovType: string;
    activityCode: ActivityCode;
    activityName?: string;
    format?: string;
    isOptionDependencyRender: boolean | undefined;
    isMultiOptionDependencyRender: boolean | undefined;
    answerValue: string | undefined;
  };
  control: Control<FieldValues>;
  validateInputs: (value: any) => boolean | string;
  getValues: (name?: string) => any;
  validateSubQuestion: (value: boolean, items?: any[], allValues?: any) => void;
  targetKeys?: string[];
  selectedKeys?: string[];
  onChange?: (
    nextTargetKeys: string[],
    direction: TransferDirection,
    moveKeys: string[],
    questionCode: string,
  ) => void;
  onSelectChange?: (
    sourceSelectedKeys: string[],
    targetSelectedKeys: string[],
  ) => void;
  onScroll?: (
    direction: TransferDirection,
    e: React.SyntheticEvent<HTMLUListElement>,
  ) => void;
  errors: any;
  handleDataChange?: any;
  dataListChange?: any[];
}

const DynamicQuestion: React.FC<DynamicQuestionProps> = ({
  sectionList,
  questionList,
  control,
  validateInputs,
  getValues,
  validateSubQuestion,
  targetKeys,
  selectedKeys,
  onChange,
  onSelectChange,
  onScroll,
  errors,
  handleDataChange,
  dataListChange,
}) => {
  
  const {
    type,
    value,
    text,
    items = [],
    dependency,
    lovType,
    activityCode,
    activityName,
    format,
    isMultiOptionDependencyRender,
    isOptionDependencyRender,
    answerValue,
  } = questionList;

  const { setExtraAnswers, extraAnswers } = useExtraQuestionsData();

  React.useEffect(() => {
    if (type === 'Questionário' && answerValue) {
      storeAnswerValueAndActivityCode(answerValue, activityCode, items);
    }
  }, [answerValue, type, activityCode, items]);

  const dependencyValue = useWatch({
    control,
    name: dependency,
  });

  let shouldRender = !dependency;

  if (dependency) {
    if (Array.isArray(dependencyValue) && dependencyValue.length > 0) {
      shouldRender = true;
    } else if (dependencyValue === true || dependencyValue === 'true') {
      shouldRender = true;
  } else if (isOptionDependencyRender === true || isMultiOptionDependencyRender === true) {
      shouldRender = true;
  }else {
      shouldRender = false;
    }
  }

  if (!shouldRender) {
    return null;
  }

  const itemsWithLovType = items.map(item => ({
    ...item,
    lovType: lovType,
  }));

  let filteredItems = itemsWithLovType;
  if (type === 'Lista' && dependency && Array.isArray(dependencyValue)) {
    filteredItems = items.filter(item =>
      dependencyValue.includes(item.parentCode),
    );
  }

  const storeAnswerValueAndActivityCode = (
    answerValue: string,
    activityCode: string,
    items: any[],
  ) => {
    if (answerValue) {
      // Verifica se já existe uma entrada com o mesmo 'activityCode'
      const existingIndex = extraAnswers.findIndex(
        item => item.code === answerValue,
      );

      let updatedExtraAnswers;

      if (existingIndex >= 0) {
        // Atualiza o 'answerValue' existente
        updatedExtraAnswers = [...extraAnswers];
        updatedExtraAnswers[existingIndex] = {
          code: answerValue,
          activity: activityCode,
        };
      } else {
        // Adiciona um novo item
        updatedExtraAnswers = [
          ...extraAnswers,
          {
            code: answerValue,
            activity: activityCode,
          },
        ];
      }

      setExtraAnswers(updatedExtraAnswers);
    } else {
      const formValues = getValues();
      const currentValues: string[] = [];

      let sectionListItemsFiltered = sectionList.items.filter(d => d.activityCode === activityCode);

      sectionListItemsFiltered.forEach((question:any) => {
        const answerCode = formValues[question.value];

        if (answerCode) {
          const answerValue = question.items.find((i:any) => i.value === answerCode);

          if (answerValue && answerValue.answerValue?.length > 0) {
            currentValues.push(answerValue.answerValue);
          }
        }
      });

      const updatedExtraAnswers = extraAnswers.filter(extraAnswer => {
        if (extraAnswer.activity === activityCode) {
          return currentValues.includes(extraAnswer.code);
        }
        return true;
      });

      setExtraAnswers(updatedExtraAnswers);
    }
  };

const sectionListOptionItems = (questionCode: string , answerCode: string| undefined) => {
  sectionListItemsUnified({
    mode: 'Option',
    questionCode,
    selection: answerCode, // string | undefined
  });
};

const sectionListMultiOptionItems = (list: any[], name: string) => {
  sectionListItemsUnified({
    mode: 'Multi Option',
    questionCode: name,
    selection: Array.isArray(list) ? list : [], // string[]
  });
};

const sectionListItemsUnified = (params: any) => {
  if (!dataListChange) return;

  const mode = params?.mode; // 'Option' | 'Multi Option'
  const questionCode = params?.questionCode;

  let selectedSet: Set<string>;
  if (mode === 'Option') {
    selectedSet = new Set(params?.selection ? [params.selection] : []);
  } else {
    const arr = Array.isArray(params?.selection) ? params.selection : [];
    selectedSet = new Set(arr);
  }

  // Escolhe a flag consoante o modo
  const FLAG = mode === 'Option' ? 'isOptionDependencyRender' : 'isMultiOptionDependencyRender';

  const processGroup = (group: any) => {
    if (!group || !Array.isArray(group.items) || group.items.length === 0) return;

    for (const option of group.items) {
      const optSelected = option?.answerValue !== undefined && selectedSet.has(option.answerValue);

      if (mode === 'Option') {
        option.isSelected = !!optSelected;
      }

      if (optSelected) {
        // Se a option está selecionada, filhos imediatos => FLAG = true
        if (Array.isArray(option.items)) {
          for (const child of option.items) {
            child[FLAG] = true;
          }
        }
      } else if (Array.isArray(option.items)) {
        // Se a option NÃO está selecionada, analisar os filhos (nível seguinte)
        for (const child of option.items) {
          const childSelected =
            child?.answerValue !== undefined && selectedSet.has(child.answerValue);

          if (childSelected) {
            // Se um filho está selecionado, netos => FLAG = true
            if (Array.isArray(child.items)) {
              for (const grand of child.items) {
                grand[FLAG] = true;
              }
            }
          } else {
            // Caso contrário, o próprio filho => FLAG = false
            child[FLAG] = false;
          }
        }
      }
    }
  };

  // Função recursiva para percorrer a árvore de items
  const recursiveItems = (node: any, allowProcessCurrent: boolean) => {
    if (allowProcessCurrent && node?.type === mode) {
      processGroup(node);
    }

    if (!node?.items || !Array.isArray(node.items) || node.items.length === 0) return;

    for (const child of node.items) {
      if (child?.type === mode) {
        processGroup(child);
      }
      // Continua a descer para apanhar profundidades maiores
      recursiveItems(child, false);
    }
  };

  // Percorre a árvore e aplica a lógica onde deve:
  for (const section of dataListChange) {
    if (!section?.items || !Array.isArray(section.items)) continue;

    for (const question of section.items) {
      const isTargetQuestion = question?.value === questionCode && question?.type === mode;

      recursiveItems(question, isTargetQuestion);
    }
  }

  handleDataChange(dataListChange);
};

  const renderQuestion = () => {
    switch (type) {
      case 'Text':
      case 'Number':
        return (
          <Input
            name={value}
            control={control}
            label={text}
            rules={{ validate: validateInputs }}
            type={type === 'Text' ? 'text' : 'number'}
            error={errors?.[value]}
          />
        );
      case 'DTYPE_NUMBER':
        return (
          <Input
            name={value}
            control={control}
            label={text}
            rules={{ validate: validateInputs }}
            type="number"
            error={errors?.[value]}
          />
        );
      case 'Pergunta':
        return (
          <RadioComponent
            activityCode={activityCode}
            activityName={activityName ?? ''}
            name={value}
            control={control}
            label={text}
            options={items.map(item => ({
              value: item.value,
              label: item.text,
              answerValue: item.answerValue,
              lovType: item.lovType,
            }))}
            onRadioClick={selectedValue => {
              const selectedOption = items.find(
                option => option.value === selectedValue,
              );
              if (selectedOption) {
                const answerValue = selectedOption.answerValue;
                storeAnswerValueAndActivityCode(answerValue, activityCode, items);
              }
              validateSubQuestion(
                selectedValue === 'true',
                items,
                getValues(value),
              );
            }}
            rules={{ validate: validateInputs }}
            error={errors?.[value]}
            variant="extraQuestion"
          />
        );
      case 'Boolean':
        return (
          <RadioComponent
            name={value}
            control={control}
            label={text}
            options={[
              { value: 'true', label: 'Sim' },
              { value: 'false', label: 'Não' },
            ]}
            onRadioClick={isTrue =>
              validateSubQuestion(isTrue, items, getValues(value))
            }
            rules={{ validate: validateInputs }}
            error={errors?.[value]}
            variant="default"
          />
        );
      case 'Option':
        return (
          <Select
            name={value}
            control={control}
            label={text}
            placeholder="Selecione uma opção"
            options={items}
            onChange={(e, selectedValue) => {         
              e.preventDefault();
              const selectedOption = items.find(
                option => option.answerValue === selectedValue,
              );

              if (selectedOption) {
                //validar se a opção selecionada era a que estava anteriormente selecionada
                const previousOption = items.find(x => x.isSelected);
               
                validateSubQuestion(
                  previousOption?.answerValue === selectedOption?.answerValue,
                  selectedOption.items,
                  getValues(value),
                );
                const answerValue = selectedOption?.answerValue;
                storeAnswerValueAndActivityCode(answerValue, activityCode, items);

                if (handleDataChange){
                  sectionListOptionItems(value, selectedOption?.answerValue ?? '');
                }
              }
              
            }}
            rules={{ validate: validateInputs }}
            error={errors?.[value]}
          />
        );
      case 'Multi Option':
        return (
          <CheckboxGroupComponent
            name={value}
            control={control}
            questionText={text}
            items={items}
            rules={{ validate: validateInputs }}
            error={errors?.[value]}
            onChecked={sectionListMultiOptionItems}
          />
        );
      case 'Lista':
        return (
          <Transfer
            name={value}
            control={control}
            label={text}
            dataSource={filteredItems}
            targetKeys={targetKeys}
            selectedKeys={selectedKeys}
            onChange={(nextTargetKeys, direction, moveKeys) =>
              onChange?.(nextTargetKeys, direction, moveKeys, value)
            }
            onSelectChange={onSelectChange}
            onScroll={onScroll}
            rules={{ required: true }}
            error={errors?.[value]}
          />
        );
      case 'Label':
        return <Subtitle text={text} format={format} />;

      case 'DTYPE_DATE':
        return (
          <Input
            name={value}
            control={control}
            label={text}
            rules={{ validate: validateInputs }}
            type="date"
            error={errors?.[value]}
          />
        );
      default:
        return null;
    }
  };

  return (
    <>
      {renderQuestion()}
      {items?.length > 0 &&
        items.map((subQuestion, index) => (       
          <DynamicQuestion
            key={`${value}-${index}`}
            sectionList={sectionList}
            questionList={subQuestion}
            control={control}
            validateInputs={validateInputs}
            getValues={getValues}
            validateSubQuestion={validateSubQuestion}
            targetKeys={targetKeys}
            selectedKeys={selectedKeys}
            onChange={onChange}
            onSelectChange={onSelectChange}
            onScroll={onScroll}
            errors={errors}
            handleDataChange={handleDataChange}
            dataListChange={dataListChange}
          />
        ))}
    </>
  );
};

export default DynamicQuestion;
